<script setup lang="ts">
import { onUnmounted, ref, watch } from 'vue'
import draggable from 'vuedraggable'
import { PopupBasicHeader } from 'wepin-vue-component'
import EditListItem from './EditListItem.vue'
import { BasicBottomButtons as BBB } from '@/components'
import { useI18nTypes, useKeyDown } from '@/composables'
import { useAccountAPIStore } from '@/stores'
import { useWalletStore } from '@/stores/wallet'
import type { Account } from '@/types'

const { t } = useI18nTypes()

const emits = defineEmits<{
  (event: 'close'): void
}>()
const walletStore = useWalletStore()
const accountAPIStore = useAccountAPIStore()

const tempAccountList = ref<Account[] | undefined>(
  walletStore.getWallet(walletStore.selectedWalletId)?.accounts,
)
watch(
  () => walletStore.selectedWalletId,
  () => {
    tempAccountList.value = walletStore.getWallet(
      walletStore.selectedWalletId,
    )?.accounts
  },
)

// TODO: 현재는 코인만 보여주는데, 추후에 토큰을 어떻게 할 것인지 몰라서 일단 둠
// const groupAccountList = ref<BaseAccount[]>(walletStore.groupAccountList)
// const fullAccountList = ref<BaseAccount[]>(walletStore.fullAccountList)

const scrollPosition = ref<number>(0)
const dragging = ref<boolean>(false)
// const preventScroll = () => {
//   const mainPage = document.querySelector('.dashboard-main') as HTMLElement
//   scrollPosition.value = scrollPosition.value + window.pageYOffset
//   mainPage.style.overflow = 'hidden'
//   mainPage.style.position = 'fixed'
//   mainPage.style.width = '100%'
//   mainPage.style.top = `-${scrollPosition.value}px`
// }
const releaseScroll = () => {
  dragging.value = false
  const mainPage = document.querySelector('.dashboard-main') as HTMLElement
  mainPage.style.removeProperty('overflow')
  mainPage.style.removeProperty('position')
  mainPage.style.removeProperty('width')
  mainPage.style.removeProperty('top')
  window.scrollTo(0, scrollPosition.value)
}
const onMove = () => {
  // 드래그시 어느지점에서 엘리먼트가 바뀔지 고민해야함
  // event.originalEvent.stopPropagation()
  // const eventY = Math.abs(event.relatedRect.top - event.originalEvent.pageY)
  // const eventY = Math.abs(event.originalEvent.pageY)
  // event.relatedRect.bottom , event.related , event.dragged
  // + event.related.offsetHeight
  // return eventY > 30 && eventY <= 50
  return true
}

const onStart = (event: CustomEvent) => {
  event.preventDefault()
  dragging.value = true
  // preventScroll()
}

onUnmounted(() => {
  if (dragging.value) {
    releaseScroll()
  }
})

const modifyAccountList = async (): Promise<void> => {
  // onExit?.()
  await accountAPIStore.updateAccountList({
    walletId: walletStore.selectedWalletId,
    accounts:
      tempAccountList.value?.map((account) => {
        return {
          id: account.id,
          label: (account as any).label,
        }
      }) ?? [],
  })
  emits('close')
  releaseScroll()
}
useKeyDown({ confirm: modifyAccountList })
</script>

<template>
  <div class="list-edit">
    <PopupBasicHeader
      :title="t('edit_list.title')"
      :exit="() => emits('close')"
    />
    <div class="list-draggable">
      <draggable
        v-model="tempAccountList"
        item-key="id"
        handle=".handle"
        ghost-class="ghost"
        animation="500"
        :move="onMove"
        direction="vertical"
        :scroll-sensitivity="250"
        :force-fallback="true"
        @start="onStart"
      >
        <template #item="{ element }">
          <div class="container">
            <EditListItem :account="element" :is-token="false" />
          </div>
        </template>
      </draggable>
      <!-- 현재 코인만 보여주게 하기로 했습니다 -->
      <!-- <draggable
        v-if="!settingStore.accountLayout.group"
        v-model="fullAccountList"
        item-key="id"
        handle=".handle"
        ghost-class="ghost"
        @start="onStart"
        animation="500"
        :move="onMove"
        direction="vertical"
        :scroll-sensitivity="250"
        :forceFallback="true"
      >
        <template #item="{ element }">
          <div class="container">
            <EditListItem :account="element" :isToken="false" />
          </div>
        </template>
      </draggable>
      <draggable
        v-else
        v-model="groupAccountList"
        item-key="id"
        handle=".handle"
        ghost-class="ghost"
        @start="onStart"
        animation="500"
        :move="onMove"
        direction="vertical"
        group="coin"
        :scroll-sensitivity="250"
        :forceFallback="true"
      >
        <template #item="{ element }">
          <div class="container">
            <EditListItem :account="element" :isToken="false" />
            <div v-if="element.tokens.length">
              <draggable
                v-model="element.tokens"
                item-key="tokenId"
                ghost-class="ghost"
                handle=".handle"
                animation="500"
                group="token"
                :scroll-sensitivity="250"
                :forceFallback="true"
              >
                <template #item="{ element }: any">
                  <EditListItem :account="element" :isToken="true" />
                </template>
              </draggable>
            </div>
          </div>
        </template>
      </draggable> -->
    </div>
    <div class="button-group">
      <BBB @cancel="() => emits('close')" @confirm="modifyAccountList" />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.list-complete-enter,
.list-complete-leave-active {
  opacity: 0;
}
.list-edit {
  z-index: 100;
  height: 100%;
  display: flex;
  align-items: stretch;
  flex-direction: column;
  min-width: 460px;
  background-color: var(--grey_fff);
  border-radius: 8px;
  box-shadow: 0px 20px 10px rgba(0, 0, 0, 0.2);
}
.list-draggable {
  -ms-overflow-style: none;
  scrollbar-width: none;
  max-height: 480px;
  overflow-y: scroll;
}
.list-draggable::-webkit-scrollbar {
  display: none;
}
.ghost {
  /* background-color: rgb(165, 162, 162); */
  visibility: visible;
  box-shadow: 0px 5px 10px rgba(0, 0, 0, 0.25);
}
.container {
  position: relative;
}
.button-group {
  width: 100%;
  padding: 10px 20px 30px;
}
</style>
